import React, { useEffect, useState } from 'react'

import Link from 'next/link'
import posthog from 'posthog-js'

import customizationData from '@data/customization.json'

import { getBasicShopifyOrderFront } from '@lib/operations/orders'
import useSubscriptions, {
	type SubscriptionWithDetails
} from '@lib/subscription-box/use-subscriptions'
import { activeRechargeSubscription, loginWithShopify } from '@lib/operations/recharge'
import { useSession } from 'next-auth/react'
import type { ShopifyBasicOrder, ShopifyBasicOrderWithSubscription } from '@lib/queries/orders/get-basic-orders-query'
import type { CustomizationPreviewConfig } from '@lib/customization/preview-params'

import MobileLogo from '@components/global/MobileLogo'
import Spinner from '@components/Spinner'
import CustomizationModal from '@components/profile/Customization/CustomizationModal/CustomizationModal'

import profileTextNodes from '@data/i18n/profile.json'
import CustomizationModalWithPartial from './CustomizationModal/CustomizationModalWithPartial'
import styles from './CustomizationModal/styles/Customization.module.scss'
import SubscriptionItem from './SubscriptionItem'
import InactiveSubscriptionItem from './InactiveSubscriptionItem'

const PREVIEW_MOCK_ORDER: ShopifyBasicOrder = {
	id: 'preview-mode',
	tags: [],
	cancelledAt: null,
	processedAt: new Date().toISOString(),
	customAttributes: [],
	shippingAddress: { address1: '', address2: '', city: '', firstName: '', lastName: '', province: '', zip: '' },
	lineItems: { nodes: [] }
}

interface CustomizationPageProps {
	initialOrder?: ShopifyBasicOrderWithSubscription | null
	activeSubscriptions?: SubscriptionWithDetails[]
	cancelledSubscriptions?: SubscriptionWithDetails[]
	previewConfig?: CustomizationPreviewConfig | null
}

// Using function declaration for the component
function CustomizationPage({
	initialOrder: initialOrderProp,
	activeSubscriptions,
	cancelledSubscriptions,
	previewConfig
}: CustomizationPageProps): React.ReactElement {
	const { data: session } = useSession()
	const { loading, revalidateAll } = useSubscriptions()

	const [initialOrder, setInitialOrder] = useState<ShopifyBasicOrderWithSubscription | null>(
		initialOrderProp || null
	)

	// Use provided subscriptions or empty arrays as fallback
	const finalActiveSubscriptions = activeSubscriptions || []
	const finalCancelledSubscriptions = cancelledSubscriptions || []

	const [isModalOpen, setIsModalOpen] = useState(false)
	const [selectedSubscriptionIdx, setSelectedSubscriptionIdx] = useState<number | null>(null)
	const [localLoading, setLocalLoading] = useState(false)
	const [orderError, setOrderError] = useState<string | null>(null)

	const selectedSubscription =
		selectedSubscriptionIdx !== null ? finalActiveSubscriptions[selectedSubscriptionIdx] : null

	// Check if the order is eligible for customization
	const isOrderEligibleForCustomization = (order: ShopifyBasicOrderWithSubscription): boolean => {
		// Check if the order has the box_customized tag
		const hasCustomizedTag = order.tags?.some((tag) => tag.toLowerCase() === 'box_customized')

		// Order is eligible if it doesn't have the customized tag
		return !hasCustomizedTag
	}

	const customizationLandingPageImage = customizationData.landing_page_image
	const customizationLandingPageFaq = customizationData.faq_link
	const customizationLandingPageSupport = customizationData.contact_support

	useEffect(() => {
		if (initialOrder) {
			if (isOrderEligibleForCustomization(initialOrder)) {
				// If the order is eligible, show the modal
				setSelectedSubscriptionIdx(null)
				setIsModalOpen(true)
				setOrderError(null)
			} else {
				// If the order is not eligible, show an error
				setIsModalOpen(false)
				setOrderError('This order has already been customized.')
			}
		}
	}, [])

	/** Only refetches subscription/order data. Use when clearing stale attrs so modal stays open. */
	const revalidateOnly = async () => {
		await revalidateAll()
	}

	const handleCustomizationSubmit = async () => {
		posthog.capture('box_customization_submitted', {
			subscription_id: selectedSubscription?.id ?? null,
			order_id: initialOrder?.id ?? null
		})
		await revalidateAll()

		// Reload the initial order if it exists
		if (initialOrder?.id) {
			const updatedOrders = await getBasicShopifyOrderFront([initialOrder.id])
			const updatedOrder = updatedOrders?.[0]
			if (updatedOrder) {
				// Find the subscription SKU from the line items
				const subscriptionSKU =
					updatedOrder.lineItems.nodes.find((item) => !!item.sellingPlan?.sellingPlanId)?.sku ||
					null

				// Create the order with subscription SKU
				const orderWithSubscription: ShopifyBasicOrderWithSubscription = {
					...updatedOrder,
					subscriptionSKU
				}

				// Update the initialOrder state with the reloaded order
				setInitialOrder(orderWithSubscription)

				if (isOrderEligibleForCustomization(orderWithSubscription)) {
					// If the order is still eligible after reloading, keep the modal open
					setSelectedSubscriptionIdx(null)
					setIsModalOpen(true)
				} else {
					// Close the modal if the order is no longer eligible
					setIsModalOpen(false)
				}
			} else {
				// Close the modal if we couldn't reload the order
				setIsModalOpen(false)
				setInitialOrder(null)
			}
		} else {
			// If no initial order, just close the modal
			setIsModalOpen(false)
			setInitialOrder(null)
		}
	}

	const [showAllInactive, setShowAllInactive] = useState(false)

	const visibleInactiveSubscriptions = showAllInactive
		? finalCancelledSubscriptions
		: finalCancelledSubscriptions.slice(0, 2)

	const handleReactivateSubscription = async (subscriptionId: number) => {
		try {
			const rechargeSession = await loginWithShopify((session as any)?.shopifyAccessToken)
			if (!rechargeSession) {
				throw new Error('Failed to get Recharge session')
			}

			await activeRechargeSubscription(rechargeSession, subscriptionId)
			posthog.capture('subscription_reactivated', { subscription_id: subscriptionId })
			await revalidateAll()
		} catch (err) {
			posthog.captureException(err)
			console.error('Failed to reactivate subscription:', err)
		}
	}

	// Preview mode — render modal directly with mock data, no Recharge writes
	if (previewConfig) {
		const noOp = async () => {}
		return (
			<div className="section">
				<div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 text-center font-medium">
					Preview Mode — You are viewing a read-only preview. No changes will be saved.
				</div>
				<CustomizationModal
					isModalOpen
					closeModal={noOp}
					subscription={null}
					shopifyOrder={PREVIEW_MOCK_ORDER}
					revalidateChargeOrder={noOp}
					revalidateOnly={noOp}
					customizationType={previewConfig.previewType}
					subscriptionProductSKU={previewConfig.previewSku}
					isPreview
				/>
			</div>
		)
	}

	// Loading spinner
	if (loading || localLoading) {
		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50 pointer-events-none">
				<Spinner />
			</div>
		)
	}

	// Show modal only if a subscription is selected or we have an eligible initial order
	let customizationModalComponent = null
	if (selectedSubscription?.lastBox) {
		if (selectedSubscription.customizationType === 'RMC' && !initialOrder) {
			const subscriptionOrderLine = selectedSubscription.lastBox?.lineItems.nodes.find(
				(lineItem) =>
					!!lineItem.sellingPlan?.sellingPlanId ||
					lineItem.sku.startsWith('bb-box') ||
					lineItem.sku.startsWith('signature-box')
			)
			const subscriptionProductSKU = selectedSubscription.sku || subscriptionOrderLine?.sku || ''
			if (!subscriptionProductSKU) {
				console.error('No SKU found for selected subscription')
				return <div>Error: No SKU found for selected subscription</div>
			}
			customizationModalComponent = (
				<CustomizationModalWithPartial
					isModalOpen={isModalOpen}
					closeModal={() => {
						setIsModalOpen(false)
						setSelectedSubscriptionIdx(null)
					}}
					subscription={selectedSubscription}
					shopifyOrder={selectedSubscription.lastBox}
					revalidateChargeOrder={handleCustomizationSubmit}
					revalidateOnly={revalidateOnly}
					customizationType="RMC"
					subscriptionProductSKU={subscriptionProductSKU}
				/>
			)
		} else {
			const subscriptionOrderLine = selectedSubscription.lastBox?.lineItems.nodes.find(
				(lineItem) =>
					!!lineItem.sellingPlan?.sellingPlanId ||
					lineItem.sku.startsWith('bb-box') ||
					lineItem.sku.startsWith('signature-box')
			)
			const subscriptionProductSKU = selectedSubscription.sku || subscriptionOrderLine?.sku || ''
			if (!subscriptionProductSKU) {
				console.error('No SKU found for selected subscription')
				return <div>Error: No SKU found for selected subscription</div>
			}
			customizationModalComponent = (
				<CustomizationModal
					isModalOpen={isModalOpen}
					closeModal={() => {
						setIsModalOpen(false)
						setSelectedSubscriptionIdx(null)
					}}
					subscription={selectedSubscription}
					shopifyOrder={selectedSubscription.lastBox}
					revalidateChargeOrder={handleCustomizationSubmit}
					revalidateOnly={revalidateOnly}
					customizationType="NMC"
					subscriptionProductSKU={subscriptionProductSKU}
				/>
			)
		}
	} else if (initialOrder && isModalOpen && isOrderEligibleForCustomization(initialOrder)) {
		// If we have an eligible initial order but no subscription, show NMC modal
		if (!initialOrder.subscriptionSKU) {
			console.error('No subscription SKU found for initial order')
			return <div>Error: No subscription SKU found for initial order</div>
		}
		customizationModalComponent = (
			<CustomizationModal
				isModalOpen={isModalOpen}
				closeModal={() => {
					setIsModalOpen(false)
					setSelectedSubscriptionIdx(null)
				}}
				subscription={null}
				shopifyOrder={initialOrder}
				revalidateChargeOrder={handleCustomizationSubmit}
				revalidateOnly={revalidateOnly}
				customizationType="NMC"
				subscriptionProductSKU={initialOrder.subscriptionSKU}
			/>
		)
	}

	return finalActiveSubscriptions.length || finalCancelledSubscriptions.length || initialOrder ? (
		<div className="section">
			{customizationModalComponent}

			{orderError && (
				<div className="container mb-6">
					<div
						className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
						role="alert"
					>
						<strong className="font-bold">Error: </strong>
						<span className="block sm:inline">{orderError}</span>
					</div>
				</div>
			)}

			<section className="customization-wrapper">
				<div className="container grid md:grid-cols-2 lg:grid-cols-2 max-lg:gap-y-10 md:gap-x-20 lg:gap-x-40">
					{/* Left Content */}
					<div className="flex flex-col order-2 md:order-1">
						{/* Header */}
						<header className="hidden md:block lg:block mb-8">
							<div className="flex items-center gap-3">
								<MobileLogo className="w-8 fill-bbx-style" />
								<h2 className="text-4xl xl:text-5xl title">
									{profileTextNodes.mySubscriptions.title}
								</h2>
							</div>
						</header>

						{/* Active Subscriptions */}
						{finalActiveSubscriptions.length > 0 && (
							<>
								<h3 className="text-lg font-medium mb-4">
									Active Subscriptions ({finalActiveSubscriptions.length})
								</h3>
								<ul>
									{finalActiveSubscriptions.map((subscription, idx) => {
										const subscriptionOrderLine = subscription.lastBox?.lineItems.nodes.find(
											(lineItem) =>
												!!lineItem.sellingPlan?.sellingPlanId ||
												lineItem.sku.startsWith('bb-box') ||
												lineItem.sku.startsWith('signature-box')
										)
										const subscriptionSKU = subscription.sku || subscriptionOrderLine?.sku || ''
										return (
											<SubscriptionItem
												key={subscription.id}
												shippingInfo={subscription.address}
												chargeShopifyOrder={subscription.lastBox || null}
												setIsModalOpen={() => {
													setSelectedSubscriptionIdx(idx)
													setIsModalOpen(true)
												}}
												numberOrders={subscription.numOrders}
												productName={subscription.product_title}
												index={idx}
												subscriptionId={subscription.id}
												isEndingSoon={subscription.isEndingSoon}
												remainingBoxes={subscription.remainingBoxes}
												subscriptionSKU={subscriptionSKU}
												isDunning={subscription.isDunning}
											/>
										)
									})}
								</ul>
							</>
						)}

						{/* Cancelled/Inactive Subscriptions */}
						{finalCancelledSubscriptions.length > 0 && (
							<>
								<h3 className="text-lg font-medium mb-4 mt-8">
									Inactive Subscriptions ({finalCancelledSubscriptions.length})
								</h3>
								<ul>
									{visibleInactiveSubscriptions.map((subscription, idx) => (
										<InactiveSubscriptionItem
											key={subscription.id}
											shippingInfo={subscription.address}
											chargeShopifyOrder={subscription.lastBox || null}
											setIsModalOpen={() => {}}
											numberOrders={subscription.numOrders}
											productName={subscription.product_title}
											subscriptionId={subscription.id}
											onReactivate={() => handleReactivateSubscription(Number(subscription.id))}
											index={finalActiveSubscriptions.length + idx}
										/>
									))}
								</ul>
								{finalCancelledSubscriptions.length > 2 && (
									<button
										type="button"
										onClick={() => setShowAllInactive(!showAllInactive)}
										className="text-bg-bbx-style underline text-sm"
									>
										{showAllInactive
											? 'Show less'
											: `Show ${finalCancelledSubscriptions.length - 2} more`}
									</button>
								)}
							</>
						)}

						<div className="text-center mt-8">
							<p className="font-bold">Need Help?</p>
							<p>
								<a
									className="underline"
									href={customizationLandingPageFaq}
									target="_blank"
									rel="noopener noreferrer"
								>
									FAQ
								</a>
								{'  '}
								or{'  '}
								<a
									className="underline"
									rel="noopener noreferrer"
									target="_blank"
									href={`mailto:${customizationLandingPageSupport}`}
								>
									Contact Support
								</a>
							</p>
						</div>
					</div>

					{/* Right Content */}
					<div className="order-1 lg:order-2">
						{/* Header */}
						<header className="block md:hidden mb-6">
							<div className="flex items-center gap-3">
								<MobileLogo className="w-8 fill-bbx-style" />
								<h2 className="text-4xl xl:text-5xl title">
									{profileTextNodes.mySubscriptions.title}
								</h2>
							</div>
						</header>

						{/* Image */}
						<figure className={styles['image-with-background']}>
							<img
								src={customizationLandingPageImage.url}
								className="relative block max-w-full w-full object-cover -top-4 -left-4 lg:-top-16 lg:-left-16"
								alt={customizationLandingPageImage.alt || 'Birchbox Image'}
							/>
						</figure>
					</div>
				</div>
			</section>
		</div>
	) : (
		<div className="container">
			<div className="mx-auto mt-6 lg:mt-12 max-w-[500px] text-center">
				<h3 className="pt-8 text-2xl mb-8">
					{/* eslint-disable-next-line react/no-unescaped-entities */}
					You don't currently have any active subscriptions, follow the link below to join
				</h3>
				<Link legacyBehavior href="/membership">
					<a className="button button-primary shrink-0 max-lg:w-full">Purchase a Subscription</a>
				</Link>
			</div>
		</div>
	)
}

export default CustomizationPage
